import { useMutation } from '@apollo/client';
import { useState } from 'react';

import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { RESOLVE_ENGAGEMENT_DISPUTE } from '../operations';
import {
  PARTIAL_RESOLVE_OUTCOME,
  checkPartialHours,
  partialHoursHelperText,
} from './partialConfirm';

export interface ResolveOutcome {
  value: string;
  label: string;
  /** Outcomes that need a confirmed-hours figure alongside the note. */
  needsHours?: boolean;
}

/**
 * The four resolution outcomes. `partially_confirmed` is spread in from
 * `partialConfirm.tsx` rather than written here, so the pending ruling on the
 * partial-hours fork stays a one-line deletion.
 *
 * `rejected` and `withdrawn` both confirm zero hours; the api reads that back
 * as `hoursConfirmed: null`.
 */
export const BASE_RESOLVE_OUTCOMES: ResolveOutcome[] = [
  { value: 'confirmed', label: 'Confirm in full' },
  PARTIAL_RESOLVE_OUTCOME,
  { value: 'rejected', label: 'Reject' },
  { value: 'withdrawn', label: 'Withdraw' },
];

interface ResolveExpanderProps {
  submissionId: string;
  /** The disputed row's submitted hours — a partial must land strictly below them. */
  hoursSubmitted: number;
  /** Upper bound for a partial: min(submitted, NTE remaining). */
  maxConfirmableHours: number;
  outcomes?: ResolveOutcome[];
  onDone: () => void;
}

/**
 * Recording the outcome of an escalated hours dispute.
 *
 * Live twin of the prototype's `ResolveDisputeExpander`, minus two things it
 * offered that the wire cannot honor: the **arbitrate toggle** (no input field
 * exists — arbitration is deferred) and the **OF-staff actor** (the mutation
 * accepts work approvers only). Neither is disabled; both are absent, so the
 * surface describes only what it can do.
 */
export default function ResolveExpander({
  submissionId,
  hoursSubmitted,
  maxConfirmableHours,
  outcomes = BASE_RESOLVE_OUTCOMES,
  onDone,
}: ResolveExpanderProps): JSX.Element {
  const [outcome, setOutcome] = useState(outcomes[0]?.value ?? 'confirmed');
  const [resolution, setResolution] = useState('');
  const [hoursText, setHoursText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resolveDispute, { loading }] = useMutation(RESOLVE_ENGAGEMENT_DISPUTE, {
    refetchQueries: ['engagementHub'],
  });

  const needsHours = outcomes.find((o) => o.value === outcome)?.needsHours === true;
  // The bound lives in partialConfirm.tsx — the only outcome that needs hours.
  const { valid: hoursValid, hoursConfirmed } = checkPartialHours(hoursText, {
    hoursSubmitted,
    maxConfirmableHours,
  });
  const trimmed = resolution.trim();
  const canSubmit = trimmed.length > 0 && (!needsHours || hoursValid) && !loading;

  async function handleSubmit() {
    setError(null);
    try {
      await resolveDispute({
        variables: {
          input: {
            submissionId,
            outcome,
            resolution: trimmed,
            ...(needsHours ? { hoursConfirmed } : {}),
          },
        },
      });
      onDone();
    } catch (err) {
      setError(
        getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? 'This dispute could not be resolved.'
      );
    }
  }

  const selectId = `resolve-outcome-${submissionId}`;

  return (
    <div className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated px-16 py-16">
      <div className="flex flex-col gap-2">
        <span className="text-on-surface subtitle-2">Resolve this dispute</span>
        <span className="text-on-surface-muted body-2">
          Recording the outcome finalizes the submission. The expert sees your note.
        </span>
      </div>

      <div className="flex flex-col gap-6">
        <label htmlFor={selectId} className="text-on-surface-muted subtitle-2">
          Outcome
        </label>
        <select
          id={selectId}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="min-h-[44px] rounded-md border border-surface-border bg-surface-card px-12 py-10 text-on-surface focus:border-brand-primary focus:outline-none"
        >
          {outcomes.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {needsHours ? (
        <Input
          label="Confirmed hours"
          value={hoursText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHoursText(e.target.value)}
          error={hoursText !== '' && !hoursValid}
          helperText={partialHoursHelperText({ hoursSubmitted, maxConfirmableHours })}
        />
      ) : null}

      <Input
        label="Resolution note"
        multiline
        rows={3}
        value={resolution}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResolution(e.target.value)}
        helperText="Explain the outcome. The expert will see this note."
      />

      {error ? <p className="text-red-600 body-2">{error}</p> : null}

      <div className="flex justify-end gap-8">
        <Button variant="tertiary" size="small" onClick={onDone}>
          Cancel
        </Button>
        <Button size="small" disabled={!canSubmit} onClick={() => void handleSubmit()}>
          Resolve dispute
        </Button>
      </div>
    </div>
  );
}
